// Albums — browse by Craving · Region · Meal · Timeline, plus the albums the user made.
import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { Link, navigate } from '../router';
import { Plus, SearchIcon } from '../icons';
import { BottomNav, ErrorBox, useToast } from '../components/Shell';
import { BookCover } from '../components/Covers';
import { NameSheet } from '../components/AlbumSheets';
import { albumHref } from '../albums';
import { namesFor } from '../lib/format';
import { monthName, t as tr, useT } from '../i18n';
import { useScript } from '../script';
import { load, save } from '../storage';
import type { AlbumsView, AlbumViewName, MyAlbum, Script, TimelineBucket } from '../types';

const VIEWS: AlbumViewName[] = ['craving', 'region', 'meal', 'timeline'];
const VIEW_KEY = 'er.albums.view';
const cacheKey = (v: AlbumViewName) => `er.cache.albums.${v}`;

function initialView(): AlbumViewName {
  const q = new URLSearchParams(location.search).get('view') as AlbumViewName | null;
  if (q && VIEWS.includes(q)) return q;
  const saved = load<AlbumViewName>(VIEW_KEY, 'craving');
  return VIEWS.includes(saved) ? saved : 'craving';
}

export function Albums() {
  const t = useT();
  const [script] = useScript();
  const [view, setView] = useState<AlbumViewName>(initialView);
  const [data, setData] = useState<AlbumsView | null>(() => load<AlbumsView | null>(cacheKey(initialView()), null));
  const [err, setErr] = useState('');
  const [naming, setNaming] = useState(false);
  const [toast, showToast] = useToast();

  const refresh = (v = view) =>
    api.albums(v).then((d) => { if (d.view === v) { setData(d); save(cacheKey(v), d); setErr(''); } }).catch((e: ApiError) => {
      if (e.status !== 401) setErr(e.message);
    });
  useEffect(() => {
    save(VIEW_KEY, view);
    history.replaceState(null, '', view === 'craving' ? '/albums' : `/albums?view=${view}`);
    const cached = load<AlbumsView | null>(cacheKey(view), null);
    if (cached) setData(cached);
    refresh(view);
  }, [view]);

  const shown = data?.view === view ? data : null;

  return (
    <div class="page with-nav" style="padding-bottom:calc(96px + env(safe-area-inset-bottom))">
      <header style="padding:16px 12px 0 20px;display:flex;align-items:center;justify-content:space-between">
        <h1 class="h1" style="font-size:30px">{t('albums.title')}</h1>
        <Link href="/search" class="icon-btn" aria-label={t('albums.search')} style="border-radius:999px;background:var(--card);border:1px solid var(--line)"><SearchIcon /></Link>
      </header>
      {data && <div style="padding:2px 20px 0;font-size:14px;color:var(--muted)">{t('albums.sub', { n: data.total })}</div>}

      <section style="padding-top:18px">
        <h2 class="section-label" style="margin:0 0 10px 20px">{t('albums.mine')}</h2>
        <div class="hrow">
          {(data?.mine ?? []).map((c) => <BookCover key={c.ref} c={c} size="mine" />)}
          <button class="book-new" onClick={() => setNaming(true)}><Plus />{t('albums.new')}</button>
        </div>
      </section>

      <section style="padding:22px 16px 0">
        <h2 class="section-label" style="margin:0 0 10px 4px">{t('albums.browse')}</h2>
        <div class="segment" role="group" aria-label={t('albums.view')}>
          {VIEWS.map((v) => <button aria-pressed={view === v} onClick={() => setView(v)}>{t(`view.${v}`)}</button>)}
        </div>
        <div style="padding:10px 4px 0;font-size:14px;line-height:1.5;color:var(--muted)">{t(`hint.${view}`)}</div>
      </section>

      {err && !shown && <ErrorBox message={err} onRetry={() => refresh()} />}
      {!shown && !err && (
        <div class="pad book-grid" style="padding-top:14px" aria-label={t('common.loading')}>
          {[1, 2, 3, 4].map(() => <div class="skeleton" style="height:176px;border-radius:6px 18px 18px 6px" />)}
        </div>
      )}

      {shown && view !== 'timeline' && (
        shown.covers && shown.covers.length > 0
          ? <div class="pad book-grid" style="padding-top:14px">{shown.covers.map((c) => <BookCover key={c.ref} c={c} size="grid" />)}</div>
          : <div class="hint-box" style="margin:14px 16px 0">{t('albums.empty')}</div>
      )}

      {shown && view === 'timeline' && (
        shown.timeline && shown.timeline.length > 0
          ? <ol class="rail">{shown.timeline.map((b) => <TimelineEntry key={b.kind + (b.month ?? '')} b={b} script={script} />)}</ol>
          : <div class="hint-box" style="margin:14px 16px 0">{t('albums.empty')}</div>
      )}

      {naming && <NameSheet onClose={() => setNaming(false)} onSaved={(a) => {
        setNaming(false);
        showToast(t('album.created'));
        navigate(albumHref(`mine:${(a as MyAlbum).id}`));
      }} />}
      {toast}
      <BottomNav current="albums" />
    </div>
  );
}

function bucketLabel(b: TimelineBucket): string {
  if (b.kind === 'month' && b.month) return monthName(b.month);
  return tr(`tl.${b.kind}` as 'tl.week' | 'tl.month_rest' | 'tl.before');
}

function TimelineEntry({ b, script }: { b: TimelineBucket; script: Script }) {
  const t = useT();
  const rest = b.count - b.peek.length - b.processing;
  const anchor = b.kind + (b.month ?? '');
  return (
    <li>
      <span class="track" aria-hidden="true"><span class={'node' + (b.current ? ' now' : '')} /><span class="line" /></span>
      <Link href={`/timeline#${anchor}`} class="entry">
        <span style="display:flex;justify-content:space-between;align-items:baseline;gap:8px">
          <span class="display" style="font-weight:700;font-size:18px">{bucketLabel(b)}</span>
          <span class="small muted" style="flex:none">{t('n.recipes', { n: b.count })}</span>
        </span>
        <span style="display:flex;flex-wrap:wrap;gap:6px">
          {b.peek.map((p) => <span class="dish-chip" lang={script === 'ta' ? 'ta' : undefined}>{namesFor(p, script)[0]}</span>)}
          {b.processing > 0 && <span class="dish-chip">{t('tl.reading', { n: b.processing })}</span>}
          {rest > 0 && <span class="dish-chip">+{rest}</span>}
        </span>
      </Link>
    </li>
  );
}
